import { useMemo } from 'react';
import {
  buildNameMapFromModel,
  filterKnownFindings,
  hasBreakingFindings,
} from '@/tools/models/compatibilityDiff.utilities';
import {
  useApiDiffModelsQuery,
  useApiGetModelVersionQuery,
  useApiGetModelVersionsQuery,
} from '@seeqdev/api/endpoint/ModelsApi';

/**
 * Fetches the compatibility diff between the current production version and a draft for
 * display-only scenarios (e.g. showing findings in the Lock confirmation modal before
 * the user opens it). Callers that need a fresh diff *after* persisting new edits should
 * use `saveDraftAndFetchCompatibility` from editModel.actions instead — that action
 * awaits the version lookup so `baseModelId` is always resolved before `diffModels`.
 *
 * Returns empty findings when there is no production version (first publish).
 */
export function useCompatibilityCheck(modelId: string | undefined, draftVersionId: string | undefined) {
  const versionsQuery = useApiGetModelVersionsQuery({ modelId: modelId! }, { enabled: !!modelId });
  const productionVersionId = versionsQuery.data?.productionVersionId;

  const diffQuery = useApiDiffModelsQuery(
    { baseModelId: productionVersionId!, candidateModelId: draftVersionId! },
    { enabled: !!productionVersionId && !!draftVersionId },
  );

  const baseModelQuery = useApiGetModelVersionQuery(
    { modelId: modelId!, versionId: productionVersionId! },
    { enabled: !!modelId && !!productionVersionId },
  );

  const candidateModelQuery = useApiGetModelVersionQuery(
    { modelId: modelId!, versionId: draftVersionId! },
    { enabled: !!modelId && !!draftVersionId },
  );

  const baseFeatureNameMap = useMemo(() => buildNameMapFromModel(baseModelQuery.data), [baseModelQuery.data]);
  const candidateFeatureNameMap = useMemo(
    () => buildNameMapFromModel(candidateModelQuery.data),
    [candidateModelQuery.data],
  );
  /**
   * Union of base + candidate, keyed by canonicalId. Covers every feature the server can
   * reference from a finding (added features come from candidate, removed from base,
   * retained from either). Candidate wins on conflict so renamed features show their
   * current name.
   */
  const featureNameMap = useMemo(
    () => new Map([...baseFeatureNameMap, ...candidateFeatureNameMap]),
    [baseFeatureNameMap, candidateFeatureNameMap],
  );

  const findings = filterKnownFindings(diffQuery.data?.findings ?? []);

  return {
    findings,
    /** Feature names for every canonicalId that can appear in a finding (base ∪ candidate). */
    featureNameMap,
    isLoading: versionsQuery.isLoading || (!!productionVersionId && !!draftVersionId && diffQuery.isLoading),
    isFirstPublish: versionsQuery.isSuccess && !productionVersionId,
    hasBreakingChanges: hasBreakingFindings(findings),
  };
}
